<script setup>
/* eslint-disable */
import { ref, computed, defineEmits } from 'vue'
import { useGameStore } from '@/store/game'
import { AuctionManager } from '../econLogic'
import { GameActions, GameActionTypes } from '@/utils/gameActions'
import { getStatGrade } from '@/utils/chocoboStats'
import { getChocoboColorClass } from '@/utils/colorUtils'
import { applyThemeClasses } from '@/utils/themeColors'
import { BreedingManager } from '@/services/breedingManager'

const emit = defineEmits(['close'])
const gameStore = useGameStore()
const mode = ref('sell')
const selectedChocobo = ref(null)
const currentBids = ref([])

const formatRegionName = (region) => {
  const regionMap = {
    'region1': 'Eastern Metropolis',
    'region2': 'Junon Area',
    'region3': 'Western Seaboard',
    'region4': 'Central Range',
    'region5': 'Mideel Forest',
    'region6': 'Northern Territory'
  }
  return regionMap[region] || region
}

// Computed properties
const playerChocobos = computed(() => {
  return gameStore.playerChocobos.filter(chocobo => {
    // Don't allow selling if only 1 chocobo left
    if (gameStore.playerChocobos.length <= 1) return false
    
    // Don't allow selling breeding chocobos
    if (!BreedingManager.isChocoboAvailable(chocobo)) return false
    
    return true
  })
})

const availableAuctions = computed(() => {
  // Get all auctions for NPCs from the game store
  return gameStore.activeAuctions.map(auction => ({
    chocobo: auction.chocobo,
    owner: getOwnerById(auction.owner.id),
    basePrice: auction.basePrice,
    urgent: auction.urgent
  })).filter(auction => auction.owner && !auction.owner.isPlayer)
})


// Methods
const calculateBaseValue = (chocobo) => {
  return AuctionManager.calculateBaseValue(chocobo)
}

const formatMoney = (amount) => {
  return amount.toLocaleString()
}

const getOwnerName = (owner) => {
  return owner?.name || 'Unknown'
}

const getOwnerById = (id) => {
  return gameStore.owners.find(owner => owner.id === id)
}

const showBids = (chocobo) => {
  selectedChocobo.value = chocobo
  // Generate bids from all non-player owners
  const nonPlayerOwners = gameStore.owners.filter(owner => !owner.isPlayer)
  currentBids.value = AuctionManager.generateBids(
    chocobo,
    nonPlayerOwners,
    gameStore
  )
}

const acceptBid = async (bid) => {
  if (!selectedChocobo.value) return
  
  try {
    const buyer = getOwnerById(bid.ownerId)
    if (!buyer) {
      console.error("Buyer not found")
      return
    }
    
    const result = AuctionManager.handleSale(
      selectedChocobo.value,
      buyer,
      gameStore.playerOwner,
      bid.amount,
      gameStore
    )

    if (result.success) {
      await GameActions.performAction(GameActionTypes.PURCHASE)
      closeModal()
    }
  } catch (error) {
    console.error('Sale failed:', error)
  }
}


const closeModal = () => {
  selectedChocobo.value = null
  currentBids.value = []
}

const placeBid = async (auction) => {
  if (!gameStore.playerOwner.money >= auction.basePrice) return
  
  try {
    const result = gameStore.handleAuctionPurchase(auction)
    if (result) {
      await GameActions.performAction(GameActionTypes.PURCHASE)
      closeModal()
      emit('close')
    }
  } catch (error) {
    console.error('Purchase failed:', error)
  }
}

</script>

<template>
  <div :class="`fixed inset-0 ${applyThemeClasses('components.modal.overlay')} flex items-center justify-center z-50`">
    <div :class="`rounded-lg ${applyThemeClasses('components.modal.container')} max-h-[90vh] overflow-y-auto p-6`">
        <!-- Header -->
        <div class="flex justify-between items-center p-6 border-b">
          <h2 :class="applyThemeClasses('text.primary')">Chocobo Auction House</h2>
          <button 
            @click="$emit('close')"
            class="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
  
        <!-- Scrollable Content -->
        <div class="flex-1 overflow-y-auto p-6">
          <!-- Auction Mode Toggle -->
          <div class="flex justify-center mb-6">
            <button 
              @click="mode = 'sell'"
              :class="[
                'px-4 py-2 rounded-l-lg',
                mode === 'sell' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200'
              ]"
            >
              Sell Chocobo
            </button>
            <button 
              @click="mode = 'buy'"
              :class="[
                'px-4 py-2 rounded-r-lg',
                mode === 'buy' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200'
              ]"
            >
              Buy Chocobo
            </button>
          </div>
  
          <!-- Sell Mode -->
          <div v-if="mode === 'sell'" class="space-y-4">
            <div v-if="playerChocobos.length === 0" class="text-center py-8 text-gray-500">
              <span :class="applyThemeClasses('text.secondaryBold')">You must keep at least one chocobo</span>
            </div>
            <!-- Player's Chocobos -->
            <div v-else v-for="chocobo in playerChocobos" :key="chocobo.id" class="border rounded-lg p-4">
              <div class="flex items-center justify-between">
                <div>
                  <h3 :class="applyThemeClasses('text.primaryLow')">{{ chocobo.name }}</h3>
                  <div class="text-sm text-gray-600">
                    <span :class="applyThemeClasses('text.secondary')">Level {{ chocobo.level }} {{ chocobo.color.charAt(0).toUpperCase() + chocobo.color.slice(1) }}</span>
                  </div>
                  <div class="mt-2 space-x-2">
                    <span :class="applyThemeClasses('text.secondaryBold')">Speed: {{ getStatGrade(chocobo.speed) }}</span>
                    <span :class="applyThemeClasses('text.secondaryBold')">Stamina: {{ getStatGrade(chocobo.stamina) }}</span>
                  </div>
                  <div class="mt-1">
                    <span v-if="chocobo.hasSpeedXFactor" 
                      class="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mr-2"
                    >
                      Speed X-Factor
                    </span>
                    <span v-if="chocobo.hasStaminaXFactor"
                      class="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full"
                    >
                      Stamina X-Factor
                    </span>
                  </div>
                </div>
  
                <div class="text-right">
                  <div class="text-lg font-bold text-green-600">
                    Base Value: {{ formatMoney(calculateBaseValue(chocobo)) }}G
                  </div>
                  <button 
                    @click="showBids(chocobo)"
                    class="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    View Bids
                  </button>
                </div>
              </div>
            </div>
          </div>
  
          <!-- Buy Mode -->
          <div v-if="mode === 'buy'" class="space-y-4">
            <div v-if="availableAuctions.length === 0" class="text-center py-8 text-gray-500">
              <span :class="applyThemeClasses('text.secondaryBold')">No chocobos currently available for purchase</span>
            </div>
            
            <div v-else v-for="auction in availableAuctions" :key="auction.chocobo.id" class="border rounded-lg p-4">
              <div class="flex items-center justify-between">
                <div>
                  <h3 :class="applyThemeClasses('text.primaryLow')">{{ auction.chocobo.name }}</h3>
                  <div class="text-sm text-gray-600">
                    <span :class="applyThemeClasses('text.secondary')">Level {{ auction.chocobo.level }} {{ auction.chocobo.color.charAt(0).toUpperCase() + auction.chocobo.color.slice(1) }}</span>
                  </div>
                  <div class="mt-2 space-x-2">
                    <span :class="applyThemeClasses('text.secondaryBold')">Speed: {{ getStatGrade(auction.chocobo.speed) }}</span>
                    <span :class="applyThemeClasses('text.secondaryBold')">Stamina: {{ getStatGrade(auction.chocobo.stamina) }}</span>
                  </div>
                  <div class="mt-1">
                    <span v-if="auction.chocobo.hasSpeedXFactor" 
                      class="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mr-2"
                    >
                      Speed X-Factor
                    </span>
                    <span v-if="auction.chocobo.hasStaminaXFactor"
                      class="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full"
                    >
                      Stamina X-Factor
                    </span>
                  </div>
                  <div class="mt-2 text-sm">
                    <span :class="applyThemeClasses('text.secondaryBold')">Seller: </span><span :class="applyThemeClasses('text.secondary')">{{ auction.owner.ranchName }}</span>
                  </div>
                </div>

                <div class="text-right">
                  <div class="text-lg font-bold text-green-600">
                    {{ auction.basePrice.toLocaleString() }}G
                  </div>
                  <div v-if="auction.urgent" class="text-sm text-red-600 mb-2">
                    Urgent Sale!
                  </div>
                  <button 
                    @click="placeBid(auction)"
                    :disabled="gameStore.playerOwner.money < auction.basePrice"
                    :class="[
                      'px-4 py-2 rounded text-sm',
                      gameStore.playerOwner.money >= auction.basePrice
                        ? 'bg-green-500 hover:bg-green-600 text-white'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    ]"
                  >
                    Purchase
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Footer -->
        <div class="p-6 border-t">
          <div class="flex justify-end">
            <button 
              @click="$emit('close')"
              class="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Close
            </button>
          </div>
        </div>
      </div>
  
      <!-- Bid Modal -->
      <div v-if="selectedChocobo" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg p-6 max-w-2xl w-full m-4 flex flex-col max-h-[90vh]">
          <!-- Bid Modal Header -->
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-xl font-bold">Offers for {{ selectedChocobo.name }}</h3>
            <button 
              @click="closeModal"
              class="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
  
          <!-- Bid Modal Content -->
          <div class="flex-1 overflow-y-auto">
            <div v-if="currentBids.length === 0" class="text-center py-8 text-gray-500">
              No offers received for this chocobo
            </div>
            
            <div v-else class="space-y-4">
              <div 
                v-for="bid in currentBids" 
                :key="bid.ownerId" 
                class="border rounded p-4 hover:bg-gray-50 transition-colors"
              >
                <div class="flex justify-between items-center">
                  <div>
                    <div class="font-semibold">{{ getOwnerName(getOwnerById(bid.ownerId)) }}</div>
                    <div class="text-sm text-gray-600">
                      {{ getOwnerById(bid.ownerId)?.ranchName }} • 
                      {{ formatRegionName(getOwnerById(bid.ownerId)?.homeRegion) }}
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="text-lg font-bold text-green-600">{{ formatMoney(bid.amount) }}G</div>
                    <button 
                      @click="acceptBid(bid)"
                      class="mt-2 px-4 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                    >
                      Accept Offer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
  
          <!-- Bid Modal Footer -->
          <div class="mt-6 flex justify-end">
            <button 
              @click="closeModal"
              class="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </template>